import { getNotificationContainer } from '../../container/notification-module'
import { NotificationService } from '../../modules/notifications/services/NotificationService'
import { MESSAGES_SERVICE_KEY, NOTIFICATION_SERVICE_KEY } from '../../modules/notifications'
import { MessageService } from '../../modules/notifications/services/MessageService'
import { getMessengerContainer } from '../../container/messenger-module'
import { ConversationService } from '../../modules/messenger/services/ConversationService'
import { CONVERSATION_SERVICE_KEY } from '../../modules/messenger'
import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { getFormContainer } from '../../container/form-module'
import { UserFormService } from '../../modules/forms/services/UserFormService'
import { USER_FORM_SERVICE_KEY } from '../../modules/forms'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { Notification as N } from '../../modules/notifications/models/Notification'
import { Message } from '../../modules/notifications/models/Message'
import styles from './List.module.css'
import { ActivityWidget } from './ActivityWidget'
import { Query, QueryParam } from '../../common/api/Query'
import { Conversation, ConversationQuery } from '../../modules/messenger/models/Conversation'
import { TransportType } from '../../common/enums/TransportType'
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@material-ui/lab'
import { forkJoin, Observable } from 'rxjs'
import { emptyUserDTO, User, UserDTO } from '../../modules/users/models/User'
import { WeekViewHome } from './WeekViewHome'
import { Box, CircularProgress, Typography } from '@mui/material'
import { height } from '@mui/system'
import { AlertWidget } from './AlertWidget'

const notificationContainer = getNotificationContainer()
const notificationService = notificationContainer.get<NotificationService>(NOTIFICATION_SERVICE_KEY)
const messageService = notificationContainer.get<MessageService>(MESSAGES_SERVICE_KEY)
const messengerContainer = getMessengerContainer()
const conversationsService = messengerContainer.get<ConversationService>(CONVERSATION_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

const notificationsPerPage = 7

type NewsWallProps = {
  id?: string
  selectedDate?: Date
}

export const NewsWall = (props: NewsWallProps) => {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()

  const [conversationCollection, setConversationCollection] = useState<Conversation[]>([])
  const [notifications, setNotifications] = useState<N[]>([])
  const [notificationMessages, setNotificationMessages] = useState<Map<string, Message>>(new Map())
  const [authors, setAuthors] = useState<Map<string, string>>(new Map())
  const [isLoading, setIsLoading] = useState<boolean>(true) // para observar animacion de recarga hay que poner true
  const [page, setPage] = useState<number>(1)
  const [user, setUser] = useState<UserDTO>(emptyUserDTO())
  const [doctor, setDoctor] = useState<UserDTO>(emptyUserDTO())
  const [isUserLoading, setIsUserLoading] = useState<boolean>(true)

  useEffect(() => {
    if (isLoading && loggedUser?.id) {
      conversationsService
        .getFilteredItems(
          new Query({
            pager: { offset: (page - 1) * notificationsPerPage, limit: notificationsPerPage },
            query: [new QueryParam<ConversationQuery>('userID10R2', loggedUser.id)],
          })
        )
        .subscribe((res) => {
          setConversationCollection(res)
        })
      notificationService
        .getFilteredList(
          new Query({
            pager: { offset: (page - 1) * notificationsPerPage, limit: notificationsPerPage },
            query: [
              new QueryParam<N>('recipientID', loggedUser?.id || ''),
              new QueryParam<N>('transportType', TransportType.App),
            ],
            sort: [{ field: 'createdAt', desc: true }],
          })
        )
        .subscribe((res) => {
          setIsLoading(false)
        })
    }
  }, [isLoading])

  useEffect(() => {
    if (!notifications.length) {
      return
    }
    const authorsTmp = new Map()
    getUsers(notifications.map((n) => n.senderID)).subscribe((res) => {
      res.filter((u) => u).map((u) => authorsTmp.set(u.id, u.firstName))
      setAuthors(authorsTmp)
    })
  }, [notifications])

  useEffect(() => {
    if (!loggedUser?.id) {
      setIsUserLoading(false)
      return
    }

    userService.getByID(loggedUser.id).subscribe((res) => {
      if (res) {
        setUser(res.toDTO())
      }
      setIsUserLoading(false)
    })
  }, [loggedUser])

  useEffect(() => {
    if (!user?.id) return
    userService.getByID(user.assignedID).subscribe((res) => {
      if (!res) return
      setDoctor(res.toDTO())
    })
  }, [user])

  const getUsers = (ids: string[]): Observable<User[]> =>
    forkJoin(ids.map((id) => userService.getByID(id))) as unknown as Observable<User[]>

  const getMessages = (ids: string[]): Observable<Message[]> =>
    forkJoin(ids.map((id) => messageService.getByID(id))) as unknown as Observable<Message[]>

  const mappedNotifications = () => {
    return notifications.map((n) => {
      return (
        <TimelineItem key={n.id}>
          <TimelineOppositeContent style={{ padding: 0 }} />
          <TimelineSeparator>
            <TimelineConnector style={{ backgroundColor: '#e5e5e5' }} />
            <TimelineDot
              variant={'outlined'}
              className={styles.timelineDot}
              style={{ borderColor: 'var(--green-primary)' }}
            />
            <TimelineConnector style={{ backgroundColor: '#e5e5e5' }} />
          </TimelineSeparator>
          <TimelineContent>
            <ActivityWidget
              id={n.id}
              source={n.notType}
              title={n.title}
              description={n.message}
              creator={authors.get(n.senderID)}
              date={n.createdAt}
              url={''}
              status={n.sendingStatus}
            />
          </TimelineContent>
        </TimelineItem>
      )
    })
  }

  if (isUserLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: 'var(--green-light)' }} />
        <Typography variant="body1" color="text.secondary">
          {t('loading')}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {user.isPatient ? (
        <Box mt={1}>
          <WeekViewHome
            id={'1'}
            selectedDate={props.selectedDate ?? new Date()}
            handlerEdit={() => {}}
            handlerRemove={() => {}}
          />
        </Box>
      ) : (
        <AlertWidget />
      )}
    </>
  )
}
